// Owner-locked unit hands. Pure state transitions for the Sorcery
// "Only It Can Cast" rule. No React, no network.
//
// ── Sorcery rule ──
// Some cards (Morgana le Fay, the four Omphalos artifacts, Archangel
// Gabriel) draw spells that end up in a SEPARATE zone, not the player's
// hand. Only the controller of that special spellcaster may look at the
// spell and cast it. If control changes, the new controller gets to look
// and cast. If the spellcaster leaves the realm, any remaining spells go
// to the cemetery.
//
// We model this as `unitHands`, a slice of match state keyed by the owner
// unit's instance id:
//
//   unitHands: {
//     [ownerInstanceId]: {
//       controller: 'p1' | 'p2',   // who may view + cast (flips on steal)
//       spells: SpellInstance[],   // each still carries _castableOnlyBy
//     }
//   }
//
// Each spell instance keeps its `_castableOnlyBy` tag (see ownerLock.js)
// so the existing cast-eligibility + routing path keeps working.
//
// These transitions are pure (return a fresh `unitHands` object); the
// host writes the result into gameState and syncs it like `piles`.

/** The unit hand for `ownerId`, or null when none exists. */
export function getUnitHand(unitHands, ownerId) {
  return unitHands?.[ownerId] || null;
}

/**
 * Route a freshly drawn owner-locked spell into its unit's hand,
 * creating the entry (with the supplied controller) on first draw.
 * No cap. Omphalos accumulates a spell per turn.
 */
export function addSpellToUnitHand(unitHands, ownerId, spell, controller, index = null) {
  const next = { ...(unitHands || {}) };
  const entry = next[ownerId];
  if (entry) {
    // Idempotent: a spell already held (e.g. a cancelled drag re-adding)
    // is not duplicated.
    if (entry.spells.some((s) => s.id === spell.id)) return unitHands || {};
    // The current controller draws it, so they have seen it (sticky reveal).
    if (!spell._seenBy) spell._seenBy = [entry.controller];
    const spells = [...entry.spells];
    // index === null appends; otherwise insert at that slot (reorder via
    // drag-back into a specific position).
    const at = index == null ? spells.length : Math.max(0, Math.min(index, spells.length));
    spells.splice(at, 0, spell);
    next[ownerId] = { controller: entry.controller, ownerArt: entry.ownerArt, spells };
  } else {
    if (!spell._seenBy) spell._seenBy = [controller];
    next[ownerId] = { controller, spells: [spell] };
  }
  return next;
}

/**
 * Remove a cast (or otherwise departed) spell from its unit hand. The
 * entry is kept even at zero spells so the controller persists and a
 * later refill (Omphalos) reuses it. The badge simply hides at count 0.
 */
export function removeSpellFromUnitHand(unitHands, ownerId, spellId) {
  const entry = unitHands?.[ownerId];
  if (!entry) return unitHands || {};
  const next = { ...unitHands };
  next[ownerId] = {
    controller: entry.controller,
    ownerArt: entry.ownerArt, // keep the badge art across an empty hand
    spells: entry.spells.filter((s) => s.id !== spellId),
  };
  return next;
}

/**
 * The owner unit left the realm: drop its entry and hand back the
 * remaining spells so the host can flush them to the controller's
 * cemetery via the standard sendCardToPile path.
 *
 * @returns {{ unitHands: object, spells: SpellInstance[] }}
 */
export function flushUnitHand(unitHands, ownerId) {
  const entry = unitHands?.[ownerId];
  if (!entry) return { unitHands: unitHands || {}, spells: [] };
  const next = { ...unitHands };
  delete next[ownerId];
  return { unitHands: next, spells: entry.spells };
}

/**
 * Per-seat sanitization. The viewer sees real faces only for unit hands
 * they currently control; every other unit hand is reduced to its
 * controller + face-down backs (count stays public, identities do not).
 * Mirrors the hidden-hand model.
 *
 * @param {object} unitHands         the full, unsanitized map
 * @param {'p1'|'p2'} viewerController the seat requesting the view
 * @param {(spell:object)=>object} sanitizeFace face-down reducer (e.g.
 *   syncBridge.sanitizeForFaceDown); identity reducer when omitted.
 */
/**
 * Merge an incoming unit-hands view with the seat's PRIOR local state.
 * Per-spell: the incoming view is authoritative for membership, order, and
 * controller (it comes from the hand's controller, who owns its contents),
 * but for each spell a local FACE is kept when we already hold one. A seat
 * only ever holds a face for a spell it is entitled to see (it drew it, or
 * received it while controlling / after seeing it), so keeping local faces
 * never over-reveals. And it stops the OTHER client, which broadcasts our
 * own controlled hand back to us as all-backs, from blanking our faces.
 */
export function mergeUnitHandViews(incoming, prior) {
  const hasFace = (s) => !!(s && (s.cardId || s.imageUrl));
  const out = {};
  const ids = new Set([...Object.keys(incoming || {}), ...Object.keys(prior || {})]);
  for (const id of ids) {
    const inc = incoming?.[id];
    const pri = prior?.[id];
    if (!inc) { out[id] = pri; continue; } // only the local copy knows this hand
    const localById = new Map((pri?.spells || []).map((s) => [s.id, s]));
    const spells = (inc.spells || []).map((s) => {
      if (s._controllerOnly) return s;
      if (hasFace(s)) return s; // incoming already carries the face
      const local = localById.get(s.id);
      return hasFace(local) ? local : s; // keep a face we already hold
    });
    out[id] = { ...inc, spells };
  }
  return out;
}

export function sanitizeUnitHandsForSeat(unitHands, viewerController, sanitizeFace) {
  const reduce = sanitizeFace || ((s) => ({ id: s.id }));
  const out = {};
  for (const [ownerId, entry] of Object.entries(unitHands || {})) {
    // ownerArt (the owner unit's card image) is public board data. It
    // rides every view so the badge never depends on a live mesh lookup.
    const base = { controller: entry.controller, ownerArt: entry.ownerArt };
    const controls = entry.controller === viewerController;
    out[ownerId] = {
      ...base,
      spells: entry.spells.map((s) => {
        // A spell's face is revealed to the controller AND to any seat that
        // has already seen it (sticky per-seat reveal. Sorcery: gaining
        // control of the hand lets a player look at its cards, and having
        // looked, they keep that knowledge). Everyone else gets a back.
        if (controls || (!s._controllerOnly && s._seenBy && s._seenBy.includes(viewerController))) return s;
        const backCard = reduce(s);
        if (s._controllerOnly) backCard._controllerOnly = true;
        if (s._returnToOwnerHand) backCard._returnToOwnerHand = true;
        // Keep _seenBy on the back so entitlement stays identical on every
        // client. The receiver never has to recompute who may look.
        if (s._seenBy) backCard._seenBy = s._seenBy;
        return backCard;
      }),
    };
  }
  return out;
}
